// components/onboarding/steps/BusinessDetailsStep.jsx - World-Class Complete
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const BusinessDetailsStep = ({ formData, setFormData, onNext, onBack }) => {
  const [errors, setErrors] = useState({});
  const [activeFeature, setActiveFeature] = useState(0);

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
  ];

  const platformFeatures = [
    {
      icon: '🤖',
      title: 'AI-Powered Smart Routing',
      description: 'Our AI selects the best courier partner for each order based on destination, weight, and delivery speed',
      stat: '45% faster delivery'
    },
    {
      icon: '📞',
      title: 'Automated NDR Calling',
      description: 'Intelligent automated calls to customers for failed deliveries, reducing RTO by up to 40%',
      stat: '40% RTO reduction'
    },
    {
      icon: '🎯',
      title: 'Smart Address Validation',
      description: 'AI validates and corrects addresses in real-time to prevent delivery failures',
      stat: '95% success rate'
    },
    {
      icon: '🇮🇳',
      title: 'Pan-India Coverage',
      description: '29,000+ pin codes covered across India with multiple carrier options',
      stat: '29,000+ pincodes'
    }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = 'Enter valid 10-digit mobile number';
    }

    // KYC Verification: At least one of Aadhaar, PAN, or GST is required
    const hasAadhaar = formData.aadhaar_number?.trim() && formData.aadhaar_number !== '********';
    const hasPAN = formData.pan_number?.trim() && formData.pan_number !== '********';
    const hasGST = formData.gst_number?.trim() && formData.gst_number !== '********';

    if (!hasAadhaar && !hasPAN && !hasGST) {
      newErrors.aadhaar_number = 'At least one KYC document is required (Aadhaar, PAN, or GST)';
      newErrors.pan_number = 'At least one KYC document is required (Aadhaar, PAN, or GST)';
      newErrors.gst_number = 'At least one KYC document is required (Aadhaar, PAN, or GST)';
    } else {
      // Validate format only if the field is provided
      if (hasAadhaar && !/^\d{12}$/.test(formData.aadhaar_number)) {
        newErrors.aadhaar_number = 'Must be 12 digits';
      }
      if (hasPAN && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan_number)) {
        newErrors.pan_number = 'Invalid PAN (e.g., ABCDE1234F)';
      }
      if (hasGST && !/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/.test(formData.gst_number)) {
        newErrors.gst_number = 'Invalid GST format';
      }
    }

    if (!formData.address_line1?.trim()) newErrors.address_line1 = 'Address required';
    if (!formData.address_city?.trim()) newErrors.address_city = 'City required';
    if (!formData.address_state?.trim()) newErrors.address_state = 'State required';
    if (!formData.address_pincode?.trim()) {
      newErrors.address_pincode = 'Pincode required';
    } else if (!/^\d{6}$/.test(formData.address_pincode)) {
      newErrors.address_pincode = '6-digit pincode required';
    }

    // Bank details validation (optional but recommended)
    // Only validate format if any bank field is filled
    const hasAnyBankData = formData.bank_account_number?.trim() || 
                          formData.bank_account_holder_name?.trim() || 
                          formData.bank_ifsc_code?.trim() || 
                          formData.bank_name?.trim() || 
                          formData.bank_branch_name?.trim();

    if (hasAnyBankData) {
      // If any bank field is filled, validate all required fields
      if (!formData.bank_account_number?.trim()) {
        newErrors.bank_account_number = 'Account number is required if providing bank details';
      } else if (!/^\d{9,18}$/.test(formData.bank_account_number)) {
        newErrors.bank_account_number = 'Account number must be 9-18 digits';
      }

      if (!formData.bank_account_holder_name?.trim()) {
        newErrors.bank_account_holder_name = 'Account holder name is required if providing bank details';
      }

      if (!formData.bank_ifsc_code?.trim()) {
        newErrors.bank_ifsc_code = 'IFSC code is required if providing bank details';
      } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.bank_ifsc_code)) {
        newErrors.bank_ifsc_code = 'Invalid IFSC code format (e.g., ABCD0123456)';
      }

      if (!formData.bank_name?.trim()) {
        newErrors.bank_name = 'Bank name is required if providing bank details';
      }

      if (!formData.bank_branch_name?.trim()) {
        newErrors.bank_branch_name = 'Branch name is required if providing bank details';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) onNext();
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: null });
  };

  // Auto-rotate features
  React.useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % platformFeatures.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="w-full max-w-7xl mx-auto"
    >
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Left: Form */}
        <div className="lg:col-span-3">
          <div className="relative bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-purple-50/50 pointer-events-none" />
            
            <div className="relative p-8 md:p-12">
              <div className="mb-8">
                <h2 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
                  Business Information
                </h2>
                <p className="text-lg text-gray-600">
                  Tell us about your business to optimize your shipping experience
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Contact */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white text-sm font-bold">1</div>
                    Contact Details
                  </h3>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={formData.phone || ''}
                      onChange={(e) => handleChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className={`w-full px-4 py-3.5 rounded-xl border-2 ${
                        errors.phone ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white/50'
                      } focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all`}
                      placeholder="9876543210"
                    />
                    {errors.phone && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 text-sm text-red-600">{errors.phone}</motion.p>}
                  </div>
                </div>

                {/* KYC Documents */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white text-sm font-bold">2</div>
                    KYC Verification
                  </h3>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4h3a3 3 0 006 0h3a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm2.5 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm2.45 4a2.5 2.5 0 10-4.9 0h4.9zM12 9a1 1 0 100 2h3a1 1 0 100-2h-3zm-1 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z" clipRule="evenodd" />
                      </svg>
                      Aadhaar Number
                      <span className="text-xs font-normal text-gray-500">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.aadhaar_number || ''}
                      onChange={(e) => handleChange('aadhaar_number', e.target.value.replace(/\D/g, '').slice(0, 12))}
                      className={`w-full px-4 py-3.5 rounded-xl border-2 ${
                        errors.aadhaar_number ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white/50'
                      } focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all`}
                      placeholder="123456789012"
                    />
                    {errors.aadhaar_number && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 text-sm text-red-600">{errors.aadhaar_number}</motion.p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                        </svg>
                        PAN Number
                        <span className="text-xs font-normal text-gray-500">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        value={formData.pan_number || ''}
                        onChange={(e) => handleChange('pan_number', e.target.value.toUpperCase().slice(0, 10))}
                        className={`w-full px-4 py-3.5 rounded-xl border-2 ${
                          errors.pan_number ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white/50'
                        } focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all`}
                        placeholder="ABCDE1234F"
                      />
                      {errors.pan_number && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 text-sm text-red-600 text-xs">{errors.pan_number}</motion.p>}
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                        </svg>
                        GST Number
                        <span className="text-xs font-normal text-gray-500">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        value={formData.gst_number || ''}
                        onChange={(e) => handleChange('gst_number', e.target.value.toUpperCase().slice(0, 15))}
                        className={`w-full px-4 py-3.5 rounded-xl border-2 ${
                          errors.gst_number ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white/50'
                        } focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all`}
                        placeholder="22AAAAA0000A1Z5"
                      />
                      {errors.gst_number && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 text-sm text-red-600 text-xs">{errors.gst_number}</motion.p>}
                    </div>
                  </div>
                  <p className="text-xs text-amber-700 bg-amber-50 p-3 rounded-lg flex items-start gap-2">
                    <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span>At least one of Aadhaar, PAN, or GST is required (providing multiple documents is highly recommended for faster verification)</span>
                  </p>
                </div>

                {/* Address */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-600 to-emerald-600 flex items-center justify-center text-white text-sm font-bold">3</div>
                    Business Address
                  </h3>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      Address Line 1
                    </label>
                    <input
                      type="text"
                      value={formData.address_line1 || ''}
                      onChange={(e) => handleChange('address_line1', e.target.value)}
                      className={`w-full px-4 py-3.5 rounded-xl border-2 ${
                        errors.address_line1 ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white/50'
                      } focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all`}
                      placeholder="Building, Street"
                    />
                    {errors.address_line1 && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 text-sm text-red-600">{errors.address_line1}</motion.p>}
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      value={formData.address_line2 || ''}
                      onChange={(e) => handleChange('address_line2', e.target.value)}
                      className="w-full px-4 py-3.5 rounded-xl border-2 border-gray-200 bg-white/50 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all"
                      placeholder="Locality, Landmark (optional)"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-2 block">City</label>
                      <input
                        type="text"
                        value={formData.address_city || ''}
                        onChange={(e) => handleChange('address_city', e.target.value)}
                        className={`w-full px-4 py-3.5 rounded-xl border-2 ${
                          errors.address_city ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white/50'
                        } focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all`}
                        placeholder="Mumbai"
                      />
                      {errors.address_city && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-1 text-xs text-red-600">{errors.address_city}</motion.p>}
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-2 block">State</label>
                      <select
                        value={formData.address_state || ''}
                        onChange={(e) => handleChange('address_state', e.target.value)}
                        className={`w-full px-4 py-3.5 rounded-xl border-2 ${
                          errors.address_state ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white/50'
                        } focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all`}
                      >
                        <option value="">Select</option>
                        {indianStates.map((state) => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                      {errors.address_state && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-1 text-xs text-red-600">{errors.address_state}</motion.p>}
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-2 block">Pincode</label>
                      <input
                        type="text"
                        value={formData.address_pincode || ''}
                        onChange={(e) => handleChange('address_pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className={`w-full px-4 py-3.5 rounded-xl border-2 ${
                          errors.address_pincode ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white/50'
                        } focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all`}
                        placeholder="400001"
                      />
                      {errors.address_pincode && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-1 text-xs text-red-600">{errors.address_pincode}</motion.p>}
                    </div>
                  </div>
                </div>

                {/* Bank Account Details */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-600 to-orange-600 flex items-center justify-center text-white text-sm font-bold">4</div>
                    Bank Account Details
                    <span className="text-sm font-normal text-gray-500 ml-2">(Recommended)</span>
                  </h3>
                  <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg flex items-start gap-2">
                    <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span>This account will be used for COD remittance. If you provide bank details, all fields are required.</span>
                  </p>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                        <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                      </svg>
                      Account Number
                    </label>
                    <input
                      type="text"
                      value={formData.bank_account_number || ''}
                      onChange={(e) => handleChange('bank_account_number', e.target.value.replace(/\D/g, ''))}
                      className={`w-full px-4 py-3.5 rounded-xl border-2 ${
                        errors.bank_account_number ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white/50'
                      } focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all`}
                      placeholder="920020056429887"
                    />
                    {errors.bank_account_number && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 text-sm text-red-600">{errors.bank_account_number}</motion.p>}
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                      </svg>
                      Account Holder Name
                    </label>
                    <input
                      type="text"
                      value={formData.bank_account_holder_name || ''}
                      onChange={(e) => handleChange('bank_account_holder_name', e.target.value)}
                      className={`w-full px-4 py-3.5 rounded-xl border-2 ${
                        errors.bank_account_holder_name ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white/50'
                      } focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all`}
                      placeholder="JP-9 GARMENTS"
                    />
                    {errors.bank_account_holder_name && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 text-sm text-red-600">{errors.bank_account_holder_name}</motion.p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                        IFSC Code
                      </label>
                      <input
                        type="text"
                        value={formData.bank_ifsc_code || ''}
                        onChange={(e) => handleChange('bank_ifsc_code', e.target.value.toUpperCase().slice(0, 11))}
                        className={`w-full px-4 py-3.5 rounded-xl border-2 ${
                          errors.bank_ifsc_code ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white/50'
                        } focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all`}
                        placeholder="UT180000819"
                      />
                      {errors.bank_ifsc_code && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 text-sm text-red-600 text-xs">{errors.bank_ifsc_code}</motion.p>}
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                        </svg>
                        Bank Name
                      </label>
                      <input
                        type="text"
                        value={formData.bank_name || ''}
                        onChange={(e) => handleChange('bank_name', e.target.value)}
                        className={`w-full px-4 py-3.5 rounded-xl border-2 ${
                          errors.bank_name ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white/50'
                        } focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all`}
                        placeholder="Axis Bank"
                      />
                      {errors.bank_name && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 text-sm text-red-600 text-xs">{errors.bank_name}</motion.p>}
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      Branch Name
                    </label>
                    <input
                      type="text"
                      value={formData.bank_branch_name || ''}
                      onChange={(e) => handleChange('bank_branch_name', e.target.value)}
                      className={`w-full px-4 py-3.5 rounded-xl border-2 ${
                        errors.bank_branch_name ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white/50'
                      } focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all`}
                      placeholder="KHARGONE BRANCH"
                    />
                    {errors.bank_branch_name && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 text-sm text-red-600">{errors.bank_branch_name}</motion.p>}
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex gap-4 pt-6">
                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    type="button"
                    onClick={onBack}
                    className="flex-1 px-8 py-4 bg-white border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all"
                  >
                    Back
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    type="submit"
                    className="flex-1 px-8 py-4 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
                  >
                    Continue
                  </motion.button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Right: Features */}
        <div className="lg:col-span-2">
          <div className="sticky top-8 space-y-6">
            {/* Rotating Feature Showcase */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeFeature}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-indigo-600 to-purple-600 p-8 text-white"
              >
                <div className="relative z-10">
                  <div className="text-6xl mb-4">{platformFeatures[activeFeature].icon}</div>
                  <h3 className="text-2xl font-bold mb-3">{platformFeatures[activeFeature].title}</h3>
                  <p className="text-blue-100 mb-4">{platformFeatures[activeFeature].description}</p>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full backdrop-blur-sm">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                    </svg>
                    <span className="font-bold">{platformFeatures[activeFeature].stat}</span>
                  </div>
                </div>
                
                {/* Progress dots */}
                <div className="flex gap-2 mt-6">
                  {platformFeatures.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveFeature(idx)}
                      className={`h-2 rounded-full transition-all ${
                        idx === activeFeature ? 'w-8 bg-white' : 'w-2 bg-white/40'
                      }`}
                    />
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Active Merchants', value: '1K+', icon: '👥' },
                { label: 'Daily Shipments', value: '50K+', icon: '📦' },
                { label: 'Pincodes Covered', value: '29K+', icon: '🇮🇳' },
                { label: 'Carrier Partners', value: '25+', icon: '🚚' }
              ].map((stat, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 * idx }}
                  className="bg-white/80 backdrop-blur-xl rounded-2xl p-5 border border-gray-100"
                >
                  <div className="text-3xl mb-2">{stat.icon}</div>
                  <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </div>

            {/* India Map SVG */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-gray-100"
            >
              <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="text-2xl">🇮🇳</span>
                Pan-India Coverage
              </h4>
              <div className="relative">
                <svg viewBox="0 0 200 200" className="w-full h-40 opacity-20">
                  <path d="M100,20 L180,60 L180,140 L100,180 L20,140 L20,60 Z" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-600" />
                  {[...Array(20)].map((_, i) => (
                    <circle key={i} cx={30 + (i % 5) * 35} cy={40 + Math.floor(i / 5) * 35} r="3" fill="currentColor" className="text-blue-600 animate-pulse" style={{ animationDelay: `${i * 0.1}s` }} />
                  ))}
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">29,000+</div>
                    <div className="text-sm text-gray-600">Pincodes Covered</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BusinessDetailsStep;
